import * as React from "react";
import { Autocomplete, TextField, CircularProgress } from "@mui/material";
import DarkTextField from "./DarkTextField";
import { AUTH_TEXT_PRIMARY } from "../theme/appTheme";

const lightFieldSx = {
  width: 280,
  "& .MuiOutlinedInput-root": {
    borderRadius: 1.5,
    fontSize: "0.8125rem",
    bgcolor: "#EBEBEB",
    "& fieldset": { borderColor: "#EBEBEB" },
    "&:hover fieldset": { borderColor: "#d8d8d8" },
    "&.Mui-focused fieldset": { borderColor: "#cfcfcf" },
  },
};

// Generic searchable id/label dropdown, backed by MUI's Autocomplete (already
// a dependency, no new package). Used for both the country and language
// pickers in registration, the profile page, and the settings dialog — all
// of which fetch { id, name, ... } rows from the backend and need to submit
// an id, not free text.
function SearchableSelect({ value, onChange, options, getOptionLabel, placeholder, disabled, dark, loading, sx }) {
  const label = getOptionLabel || ((option) => option?.name || "");
  const selected = options.find((option) => option.id === value) || null;

  const endAdornment = (params) => (
    <>
      {loading ? <CircularProgress size={16} sx={{ color: dark ? "#9ca3af" : "inherit" }} /> : null}
      {params.InputProps.endAdornment}
    </>
  );

  return (
    <Autocomplete
      fullWidth
      size="small"
      disableClearable
      options={options}
      value={selected}
      onChange={(event, newValue) => {
        // disableClearable keeps the "x" off the UI, but Autocomplete can
        // still fire onChange(null) on some keyboard/blur paths — every
        // current use of this component (country/language) is a required
        // field, so just ignore an attempt to clear rather than propagate it.
        if (newValue) onChange(newValue.id);
      }}
      getOptionLabel={label}
      isOptionEqualToValue={(option, val) => option.id === val?.id}
      disabled={disabled}
      loading={loading}
      sx={sx}
      renderInput={(params) =>
        dark ? (
          <DarkTextField
            {...params}
            placeholder={placeholder}
            InputProps={{ ...params.InputProps, endAdornment: endAdornment(params) }}
          />
        ) : (
          <TextField
            {...params}
            placeholder={placeholder}
            size="small"
            sx={lightFieldSx}
            InputProps={{ ...params.InputProps, endAdornment: endAdornment(params) }}
          />
        )
      }
      ListboxProps={{ sx: { fontSize: "0.8125rem" } }}
      componentsProps={{ paper: { sx: { color: AUTH_TEXT_PRIMARY } } }}
    />
  );
}

export default SearchableSelect;
